import { first, text } from '../core/dom.js';

/**
 * Quiz events (spec §1).
 *
 * The dnk-quiz plugin dispatches these itself through the cooperative
 * `trackdaddy:event` channel — it is the only code that knows the real step index,
 * question id and result segment. When it does, it sets `window.dnkqzTrackdaddy` and
 * everything below stands down.
 *
 * The fallback exists because quiz_started is currently *inverted*: it reports fewer
 * unique visitors than quiz_completed, which means the real trigger is being missed.
 * Rather than trust one implementation, the tracker can derive the funnel from the DOM
 * as well — quiz_started on the first interaction, never on page view.
 */

export const QUIZ_WIDGET = '.dnkqz-quiz-widget';

export function collectQuiz(tracker, { win = window, doc = document } = {}) {
  const widget = first(QUIZ_WIDGET, doc);

  if (!widget) {
    return null;
  }

  // Checked again before every emission, not just here. dnk-quiz sets the flag inside a
  // jQuery(fn) block, so it lands at DOM-ready — after this bundle has parsed and run.
  // Reading it once at boot therefore always saw `undefined` and both implementations
  // reported the whole funnel.
  const bridgeIsReporting = () => Boolean(win.dnkqzTrackdaddy);

  if (bridgeIsReporting()) {
    // The quiz reports for itself, with better data than the DOM can give us.
    return null;
  }

  // String, always. dnk-quiz reads the same attributes with jQuery .data(), which coerces
  // an all-digit value to a Number — and `properties` is a ClickHouse JSON column whose
  // path type is inferred once, so the second emitter's rows would be dropped.
  const quizId = String(widget.getAttribute('data-quiz-id') || 'dnk');
  const state = { started: false, lastStep: null };

  const start = () => {
    if (state.started || bridgeIsReporting()) {
      return;
    }
    state.started = true;

    // `quiz_version` is deliberately absent: no dnk-quiz branch emits data-quiz-version,
    // so it would only ever be the empty string — a JSON path that can never hold
    // anything else. Add it back when the attribute exists, as a String.
    tracker.track(
      'quiz_started',
      { quiz_id: quizId, quiz_type: 'dnk' },
      { once: 'session', dedupeKey: 'quiz_started:' + quizId }
    );

    reportStep();
  };

  // First interaction — a click on "start", or an answer. Never the page view.
  ['change', 'click'].forEach((type) => {
    widget.addEventListener(type, (event) => {
      if (type === 'click' && !event.target.closest('button, .dnkqz-radio, .dnkqz-checkbox, input, select')) {
        return;
      }

      start();
    });
  });

  const reportStep = () => {
    const progress = readProgress(widget);

    if (!progress || progress.current === state.lastStep || bridgeIsReporting()) {
      return;
    }

    state.lastStep = progress.current;

    tracker.track(
      'quiz_step_viewed',
      {
        quiz_id: quizId,
        step_number: progress.current,
        total_steps: progress.total,
        question_id: questionId(widget),
      },
      { once: 'session', dedupeKey: 'quiz_step_viewed:' + quizId + ':' + progress.current }
    );
  };

  widget.addEventListener('click', (event) => {
    if (!event.target.closest('#dnkqz-next') || bridgeIsReporting()) {
      return;
    }

    start();

    const progress = readProgress(widget);

    tracker.track('quiz_step_answered', {
      quiz_id: quizId,
      step_number: progress ? progress.current : 0,
      total_steps: progress ? progress.total : 0,
      question_id: questionId(widget),
      answer_value: selectedAnswer(widget),
    });

    // quiz_completed. Answering the last question is the only completion signal the DOM
    // offers: dnk-quiz shows a loader and then redirects away, leaving no result view to
    // observe. `result_segment` is therefore absent here — the recommended SKU exists only
    // in the AJAX response, which is why the quiz's own bridge reports it better. Without
    // this, switching from v1 to v2 would silently drop an event that is alive today.
    //
    // The bridge derives the same key, and this click necessarily happens after DOM-ready
    // — so the bridgeIsReporting() guard above has stood the fallback down by now. That
    // guard is what stops the fallback firing first and suppressing the richer copy as a
    // duplicate; without it `result_segment` was lost on every completion.
    if (progress && progress.total > 0 && progress.current >= progress.total) {
      tracker.track(
        'quiz_completed',
        {
          quiz_id: quizId,
          total_steps: progress.total,
          // Not `source`: the envelope reserves that for browser/server provenance and
          // would drop this without an error. This says which quiz implementation
          // reported the completion.
          reporter: 'dom_fallback',
        },
        { once: 'session', dedupeKey: 'quiz_completed:' + quizId }
      );
    }
  });

  if (typeof win.MutationObserver === 'function') {
    new win.MutationObserver(reportStep).observe(widget, { childList: true, subtree: true });
  }

  return state;
}

function readProgress(widget) {
  const node = widget.querySelector('.dnkqz-progress__text');

  if (!node) {
    return null;
  }

  const match = (node.textContent || '').match(/(\d+)\s*\/\s*(\d+)/);

  return match ? { current: parseInt(match[1], 10), total: parseInt(match[2], 10) } : null;
}

function questionId(widget) {
  const input = widget.querySelector('.dnkqz-answer-input, input[type="radio"], input[type="checkbox"]');

  if (input) {
    return String(input.getAttribute('data-field-id') || input.name || '');
  }

  return text(widget.querySelector('.dnkqz-question-label'), 60);
}

/**
 * Only the chosen option's value — never free-text input, which can hold a name or an
 * e-mail address.
 */
function selectedAnswer(widget) {
  const checked = widget.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');

  if (checked.length) {
    return Array.prototype.map.call(checked, (input) => String(input.value)).join(',').slice(0, 120);
  }

  const select = widget.querySelector('select.dnkqz-answer-input');

  return select ? String(select.value).slice(0, 120) : '';
}
